import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;
  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.refs.resetButton.addEventListener('click', () => {
      this.resetSearch();
    });

    onDocumentLoaded(() => {
      this.resetSearch(false);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleModalClick = (event) => {
    const target = event.target;
    const isInteractive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractive) {
      this.refs.searchInput.focus();
    }
  };

  get #allResultsItems() {
    return Array.from(this.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems.length) return;

    this.#allResultsItems.forEach((item, i) => {
      item.classList.toggle('keyboard-focus', i === index && this.#isKeyboardNavigation);
      item.toggleAttribute('aria-selected', i === index);
    });

    if (index >= 0) {
      this.#allResultsItems[index].scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest'
      });
    }

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.resetSearch();
      return;
    }

    if (!this.#allResultsItems.length) return;

    const max = this.#allResultsItems.length - 1;

    if (event.key === 'ArrowDown') {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = this.#currentIndex < max ? this.#currentIndex + 1 : 0;
    }

    if (event.key === 'ArrowUp') {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : max;
    }

    if (event.key === 'Enter') {
      if (this.#currentIndex >= 0) {
        event.preventDefault();
        this.#currentItem?.querySelector('a')?.click();
      }
    }
  };

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');

    const abortController = this.#createAbortController();

    const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    if (abortController.signal.aborted) return;

    morph(this.refs.predictiveSearchResults, markup);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const controller = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = controller;
    return controller;
  }

  async #resetSearch() {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'custom-liquid-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    this.dialog?.close(); // ✅ THIS CLOSES THE BAR

    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const markup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsed = new DOMParser().parseFromString(markup, 'text/html')
      .querySelector('#predictive-search-results');

    if (!parsed) return;

    morph(predictiveSearchResults, parsed);
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}